import {
    Box,
    Button,
    Divider,
    Slider,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    FillCavitiesIcon,
    RemoveSmallPartsIcon,
    RingIcon,
    SmoothIcon,
} from '~/assets/workspace-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import NumberInput from '~/components/Inputs/NumberInput';
import { useRecoilState } from 'recoil';
import FloatingPanel from '~/components/toolbar/shared/FloatingPanel';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useStructureToolGating } from '~/components/toolbar/shared/useStructureToolGating';
import { applyGaussianSmoothingGL } from '~/engine/util/algorithms/sdf/GaussianSmoothingGL';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import {
    openFloatingPanelState,
    operationPanelState,
} from '~/state/atoms/toolsAtoms';
import { useRoiSdfOperation } from './useRoiSdfOperation';

const DEFAULT_SMOOTHING_LEVEL = 3;
const DEFAULT_MIN_VOLUME_CM3 = 0.5;

// ── Remove Small Parts floating panel content ───────────────────────

export function RemoveSmallPartsPanel() {
    const { isProcessing, executeOperation } = useRoiSdfOperation();
    const [minVolumeCm3, setMinVolumeCm3] = useState(DEFAULT_MIN_VOLUME_CM3);

    const handleMinVolumeChange = (value: number | null) => {
        if (value !== null) {
            setMinVolumeCm3(value);
        }
    };

    const handleApply = () =>
        executeOperation({
            operationFn: async (roi) => {
                const result = await SDFOperations.applyRemoveSmallParts(
                    roi.texture!,
                    minVolumeCm3
                );
                if (!result) {
                    return undefined;
                }
                return {
                    texture: result.texture,
                    successValues: {
                        removed: result.numComponentsRemoved,
                        total: result.numComponentsFound,
                    },
                };
            },
            operationName: 'Remove Small Parts',
            busyMessageId: 'postProcessing.removeSmallParts.busy',
            successMessageId: 'postProcessing.removeSmallParts.success',
            failedMessageId: 'postProcessing.removeSmallParts.failed',
            logLabel: 'Remove small parts',
        });

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="subtitle2">
                <FormattedMessage id="postProcessing.removeSmallParts" />
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
                <NumberInput
                    label={
                        <FormattedMessage id="postProcessing.removeSmallParts.minVolume" />
                    }
                    value={minVolumeCm3}
                    onChange={handleMinVolumeChange}
                    min={0}
                    step={0.1}
                    precision={2}
                    fullWidth
                    disabled={isProcessing}
                />
                <Typography variant="body2" sx={{ flexShrink: 0 }}>
                    cm³
                </Typography>
            </Box>
            <Button
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
                onClick={handleApply}
                disabled={isProcessing}
                sx={{ textTransform: 'none', fontSize: 12 }}
            >
                {isProcessing ? (
                    <FormattedMessage id="postProcessing.applying" />
                ) : (
                    <FormattedMessage id="postProcessing.removeSmallParts.apply" />
                )}
            </Button>
        </Box>
    );
}

// ── Fill Cavities floating panel content ────────────────────────────

export function FillCavitiesPanel() {
    const { isProcessing, executeOperation } = useRoiSdfOperation();

    const handleApply = () =>
        executeOperation({
            operationFn: async (roi) => {
                const processedTexture = await SDFOperations.applyFillCavities(
                    roi.texture!
                );
                if (!processedTexture) {
                    return undefined;
                }
                return { texture: processedTexture };
            },
            operationName: 'Fill Cavities',
            busyMessageId: 'postProcessing.fillCavities.busy',
            successMessageId: 'postProcessing.fillCavities.success',
            failedMessageId: 'postProcessing.fillCavities.failed',
            logLabel: 'Fill cavities',
        });

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="subtitle2">
                <FormattedMessage id="postProcessing.fillCavities" />{' '}
                <Typography
                    component="span"
                    variant="subtitle2"
                    color="text.secondary"
                >
                    (3D)
                </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="postProcessing.fillCavities.description" />
            </Typography>
            <Button
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
                onClick={handleApply}
                disabled={isProcessing}
                sx={{ textTransform: 'none', fontSize: 12 }}
            >
                {isProcessing ? (
                    <FormattedMessage id="postProcessing.applying" />
                ) : (
                    <FormattedMessage id="postProcessing.fillCavities.apply" />
                )}
            </Button>
        </Box>
    );
}

// ── Smoothing floating panel content ────────────────────────────────

function SmoothingPanel() {
    const intl = useIntl();
    const { isProcessing, executeOperation } = useRoiSdfOperation();
    const [smoothingLevel, setSmoothingLevel] = useState(
        DEFAULT_SMOOTHING_LEVEL
    );

    const handleSliderChange = (_event: Event, value: number | number[]) => {
        setSmoothingLevel(value as number);
    };

    const handleSmoothing = () =>
        executeOperation({
            operationFn: async (roi) => {
                const smoothedTexture = await applyGaussianSmoothingGL(
                    roi.texture!,
                    smoothingLevel
                );
                if (!smoothedTexture) {
                    return undefined;
                }
                return {
                    texture: smoothedTexture,
                    successValues: { level: smoothingLevel },
                };
            },
            operationName: 'Gaussian Smoothing',
            busyMessageId: 'postProcessing.smoothing.busy',
            successMessageId: 'postProcessing.smoothing.success',
            failedMessageId: 'postProcessing.smoothing.failed',
            logLabel: 'Gaussian smoothing',
        });

    const handleSmoothPreserveVolume = () =>
        executeOperation({
            operationFn: async (roi) => {
                const closedTexture =
                    await SDFOperations.applyMorphologicalClosing(
                        roi.texture!,
                        smoothingLevel
                    );
                if (!closedTexture) {
                    return undefined;
                }
                return {
                    texture: closedTexture,
                    successValues: { level: smoothingLevel },
                };
            },
            operationName: 'Surface Smoothing',
            busyMessageId: 'postProcessing.smoothing.preserveVolume.busy',
            successMessageId: 'postProcessing.smoothing.preserveVolume.success',
            failedMessageId: 'postProcessing.smoothing.preserveVolume.failed',
            logLabel: 'Surface smoothing',
        });

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="subtitle2">
                <FormattedMessage id="postProcessing.smoothing" />
            </Typography>
            <Slider
                value={smoothingLevel}
                onChange={handleSliderChange}
                step={1}
                marks
                min={1}
                max={5}
                disabled={isProcessing}
                valueLabelDisplay="auto"
                size="small"
            />
            <Divider />
            <Tooltip
                title={intl.formatMessage({
                    id: 'postProcessing.smoothing.tooltip',
                })}
                placement="left"
            >
                <span>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={handleSmoothing}
                        disabled={isProcessing}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                        {isProcessing ? (
                            <FormattedMessage id="postProcessing.applying" />
                        ) : (
                            <FormattedMessage id="postProcessing.smoothing.apply" />
                        )}
                    </Button>
                </span>
            </Tooltip>
            <Tooltip
                title={intl.formatMessage({
                    id: 'postProcessing.smoothing.preserveVolume.tooltip',
                })}
                placement="left"
            >
                <span>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={handleSmoothPreserveVolume}
                        disabled={isProcessing}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                        {isProcessing ? (
                            <FormattedMessage id="postProcessing.applying" />
                        ) : (
                            <FormattedMessage id="postProcessing.smoothing.preserveVolume.apply" />
                        )}
                    </Button>
                </span>
            </Tooltip>
        </Box>
    );
}

// ── Main section ────────────────────────────────────────────────────

export default function PostProcessingSection() {
    const {
        isPointRoi,
        isStructureSetApproved,
        isSelectionApproved,
        isBulkSelectionActive,
        hasSelectedRoi,
    } = useStructureToolGating();
    const intl = useIntl();
    const [panelState, setPanelState] = useRecoilState(operationPanelState);

    const noRoiSelected = !hasSelectedRoi || isPointRoi;

    const isPostProcessingDisabled =
        noRoiSelected || isSelectionApproved || isBulkSelectionActive;

    const approvedReason = intl.formatMessage({ id: 'tool.disabled.approved' });

    const fillCavitiesAnchorRef = useRef<HTMLElement | null>(null);
    const removeSmallPartsAnchorRef = useRef<HTMLElement | null>(null);
    const smoothingAnchorRef = useRef<HTMLElement | null>(null);

    const handleRingToggle = useCallback(() => {
        setPanelState((prev) =>
            prev.type === 'ring' ? { type: 'closed' } : { type: 'ring' }
        );
    }, [setPanelState]);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    useEffect(() => {
        if (
            isPostProcessingDisabled &&
            (openPanel === 'fill-cavities' ||
                openPanel === 'remove-small-parts' ||
                openPanel === 'smoothing')
        ) {
            setOpenPanel(null);
        }
    }, [isPostProcessingDisabled, openPanel, setOpenPanel]);

    // Ring writes a new ROI, so source-ROI approval doesn't block it; only
    // a missing ROI selection or an approved structure set does.
    useEffect(() => {
        if (
            (noRoiSelected || isStructureSetApproved) &&
            panelState.type === 'ring'
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [noRoiSelected, isStructureSetApproved, panelState.type, setPanelState]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<FillCavitiesIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.fillCavities.tooltip' }),
                    isSelectionApproved ? approvedReason : undefined
                )}
                tooltipPlacement="left"
                disabled={isPostProcessingDisabled}
                panelId="fill-cavities"
                anchorRef={fillCavitiesAnchorRef}
            />
            <FloatingPanel
                panelId="fill-cavities"
                anchorEl={fillCavitiesAnchorRef.current}
                placement="left-start"
            >
                <FillCavitiesPanel />
            </FloatingPanel>

            <ToolbarButton
                icon={<RemoveSmallPartsIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({
                        id: 'toolbar.removeSmallParts.tooltip',
                    }),
                    isSelectionApproved ? approvedReason : undefined
                )}
                tooltipPlacement="left"
                disabled={isPostProcessingDisabled}
                panelId="remove-small-parts"
                anchorRef={removeSmallPartsAnchorRef}
            />
            <FloatingPanel
                panelId="remove-small-parts"
                anchorEl={removeSmallPartsAnchorRef.current}
                placement="left-start"
            >
                <RemoveSmallPartsPanel />
            </FloatingPanel>

            <ToolbarButton
                icon={<SmoothIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.smoothing.tooltip' }),
                    isSelectionApproved ? approvedReason : undefined
                )}
                tooltipPlacement="left"
                disabled={isPostProcessingDisabled}
                panelId="smoothing"
                anchorRef={smoothingAnchorRef}
            />
            <FloatingPanel
                panelId="smoothing"
                anchorEl={smoothingAnchorRef.current}
                placement="left-start"
            >
                <SmoothingPanel />
            </FloatingPanel>

            {/* Ring writes a new ROI, so source-ROI approval doesn't block it,
                but an approved structure set rejects new ROIs. */}
            <ToolbarButton
                icon={<RingIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.ring.tooltip' }),
                    isStructureSetApproved ? approvedReason : undefined
                )}
                tooltipPlacement="left"
                selected={panelState.type === 'ring'}
                disabled={
                    noRoiSelected ||
                    isStructureSetApproved ||
                    isBulkSelectionActive
                }
                onClick={handleRingToggle}
            />
        </Box>
    );
}
